import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  defaults,
  ScriptableContext,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

defaults.font.family = "Montserrat";

const options = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top" as const,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      min: 0,
    },
  },
  borderWidth: 1,
};

export type AreaChartProps = {
  data: number[][],
  labels: string[]
  datasets: string[]
};

const BACKGROUND_MAPPING = [{
  borderColor: "rgb(6, 178, 218)",
  gradient1: "rgba(5, 219, 174, 0.1)",
  gradient2: "rgba(6, 178, 218, 0.2)",
}, {
  borderColor: "rgb(215, 88, 223)",
  gradient1: "rgba(110, 111, 217, 0.1)",
  gradient2: "rgba(215, 88, 223, 0.2)"
}, {
  borderColor: "rgb(255, 106, 0)",
  gradient1: "rgba(255, 178, 127, 0.1)",
  gradient2: "rgba(255, 106, 0, 0.2)"
}, {
  borderColor: "rgb(7, 200, 184)",
  gradient1: "rgba(60, 228, 148, 0.1)",
  gradient2: "rgba(7, 200, 184, 0.2)"
}, {
  borderColor: "rgb(233, 64, 105)",
  gradient1: "rgba(224, 27, 110, 0.1)",
  gradient2: "rgba(233, 64, 105, 0.2)"
}];

function getMapping(idx: number) {
  return BACKGROUND_MAPPING[idx % BACKGROUND_MAPPING.length];
}

export default function AreaChart({ data: dataProps, labels, datasets }: AreaChartProps) {
  const data = useMemo(
    () => ({
      labels,
      datasets: dataProps.map((d, i) => ({
        fill: true,
        data: d,
        borderColor: getMapping(i).borderColor,
        backgroundColor: (context: ScriptableContext<"line">) => {
          const ctx = context.chart.ctx;
          const chartArea = context.chart.chartArea;
          const gradient = ctx.createLinearGradient(0, chartArea?.bottom || 0, 0, chartArea?.top || 400);
          gradient.addColorStop(0, getMapping(i).gradient1);
          gradient.addColorStop(0.5, getMapping(i).gradient2);

          return gradient;
        },
        point: false,
        label: datasets[i]
      }))
    }),
    [dataProps]
  );

  return <Line options={options} data={data} />;
}
